import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PreRegistration } from '@cloudsign/shared';
import type { Group, Site } from '@cloudsign/shared';
import { Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { TimeInput, isTimeOfDay, isValidEmail } from '../../components/phase2';
import { Dialog } from '../fields-notices/Dialog';
import { useGroupMembers, preKey } from './api';
import { localToUtcIso, todayIn } from '../../lib/zoneTime';

interface VisitorRow {
  name: string;
  email: string;
  phone: string;
  company: string;
}
const blank = (): VisitorRow => ({ name: '', email: '', phone: '', company: '' });
const MAX_VISITORS = 50;

/** "Pre-register": one or many visitors for the same visit; date and time are the site's local time. */
export function CreateDialog({ sites, groups, onClose }: { sites: Site[]; groups: Group[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const visitorGroups = groups.filter((g) => g.kind === 'standard');
  const hostGroups = groups.filter((g) => g.kind === 'repeat');
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '');
  const [groupId, setGroupId] = useState(visitorGroups[0]?.id ?? groups[0]?.id ?? '');
  const zone = sites.find((s) => s.id === siteId)?.timeZone ?? 'Europe/London';
  const [date, setDate] = useState(() => todayIn(zone));
  const [time, setTime] = useState('09:00');
  const [lastDay, setLastDay] = useState('');
  const [hostGroupId, setHostGroupId] = useState(hostGroups[0]?.id ?? '');
  const [hostId, setHostId] = useState('');
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [rows, setRows] = useState<VisitorRow[]>([blank()]);
  const [tried, setTried] = useState(false);
  const hosts = useGroupMembers(hostGroupId);

  useEffect(() => {
    setHostId('');
  }, [hostGroupId]);

  const setRow = (i: number, patch: Partial<VisitorRow>) => setRows((r) => r.map((x, n) => (n === i ? { ...x, ...patch } : x)));
  const rowProblems = rows.map((r) => ({
    name: r.name.trim() === '' ? t('prereg.create.nameRequired') : undefined,
    email: r.email.trim() !== '' && !isValidEmail(r.email.trim()) ? t('prereg.create.emailInvalid') : undefined,
  }));
  const timeBad = !isTimeOfDay(time) || date === '';
  const lastBad = lastDay !== '' && lastDay < date;
  const invalid = rowProblems.some((p) => p.name || p.email) || timeBad || lastBad || siteId === '' || groupId === '';

  const create = useMutation({
    mutationFn: async () => {
      const common = {
        siteId,
        groupId,
        hostMemberId: hostId === '' ? null : hostId,
        expectedAt: localToUtcIso(date, time, zone),
        expectedUntil: lastDay === '' ? null : localToUtcIso(lastDay, '23:59', zone),
        notes,
        sendEmail,
      };
      const person = (r: VisitorRow) => ({
        visitorName: r.name.trim(),
        visitorEmail: r.email.trim() === '' ? null : r.email.trim(),
        visitorPhone: r.phone.trim() === '' ? null : r.phone.trim(),
        visitorCompany: r.company.trim() === '' ? null : r.company.trim(),
      });
      if (rows.length === 1 && rows[0]) return api.post('/pre-registration', { body: { ...common, ...person(rows[0]) }, schema: PreRegistration }).then((r) => [r]);
      const { siteId: s, groupId: g, hostMemberId, expectedAt, expectedUntil, notes: n, sendEmail: se } = common;
      return api.post('/pre-registration/bulk', {
        body: { common: { siteId: s, groupId: g, hostMemberId, expectedAt, expectedUntil, notes: n, sendEmail: se }, visitors: rows.map(person) },
        schema: z.array(PreRegistration),
      });
    },
    onSuccess: (made) => {
      const pending = made.filter((m) => m.status === 'pending_approval').length;
      toast(pending > 0 ? t('prereg.create.donePending', { count: made.length, pending }) : t('prereg.create.done', { count: made.length }));
      void qc.invalidateQueries({ queryKey: preKey });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!invalid) create.mutate();
  };

  return (
    <Dialog title={t('prereg.create.title')} onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-5" noValidate>
        {create.error ? <Alert>{errorMessage(create.error)}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label={t('prereg.field.site')} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
          <SelectField label={t('prereg.field.group')} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </SelectField>
          <TextField label={t('prereg.field.date')} type="date" value={date} onChange={(e) => setDate(e.target.value)} error={tried && date === '' ? t('prereg.create.dateRequired') : undefined} />
          <TimeInput label={t('prereg.field.time')} value={time} onChange={setTime} hint={t('prereg.create.zone', { zone })} />
          <TextField
            label={t('prereg.field.lastDay')}
            hint={t('prereg.field.lastDayHint')}
            type="date"
            min={date}
            value={lastDay}
            onChange={(e) => setLastDay(e.target.value)}
            error={lastBad ? t('prereg.create.lastDayBefore') : undefined}
          />
          <div className="grid gap-4 sm:grid-cols-1">
            {hostGroups.length > 1 ? (
              <SelectField label={t('prereg.field.hostGroup')} value={hostGroupId} onChange={(e) => setHostGroupId(e.target.value)}>
                {hostGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </SelectField>
            ) : null}
            <SelectField label={t('prereg.field.host')} value={hostId} onChange={(e) => setHostId(e.target.value)} disabled={hostGroupId === ''}>
              <option value="">{t('prereg.field.noHost')}</option>
              {(hosts.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
            </SelectField>
          </div>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('prereg.create.visitors')}</legend>
          <p className="text-xs text-slate-600">{t('prereg.create.visitorsHint', { max: MAX_VISITORS })}</p>
          {rows.map((r, i) => (
            <div key={i} role="group" aria-label={t('prereg.create.visitorN', { n: i + 1 })} className="grid gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] sm:items-start">
              <TextField label={t('prereg.field.name')} value={r.name} maxLength={120} onChange={(e) => setRow(i, { name: e.target.value })} error={tried ? rowProblems[i]?.name : undefined} />
              <TextField label={t('prereg.field.email')} type="email" value={r.email} onChange={(e) => setRow(i, { email: e.target.value })} error={rowProblems[i]?.email} />
              <TextField label={t('prereg.field.phone')} type="tel" value={r.phone} onChange={(e) => setRow(i, { phone: e.target.value })} />
              <TextField label={t('prereg.field.company')} value={r.company} maxLength={120} onChange={(e) => setRow(i, { company: e.target.value })} />
              {rows.length > 1 ? (
                <button
                  type="button"
                  aria-label={t('prereg.create.removeVisitor', { n: i + 1 })}
                  onClick={() => setRows((x) => x.filter((_, n) => n !== i))}
                  className="inline-flex h-10 w-10 items-center justify-center self-end rounded-md text-slate-600 hover:bg-slate-100"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </button>
              ) : (
                <span />
              )}
            </div>
          ))}
          <Button type="button" disabled={rows.length >= MAX_VISITORS} onClick={() => setRows((r) => [...r, blank()])}>
            <Plus aria-hidden="true" className="mr-1 h-4 w-4" />
            {t('prereg.create.addVisitor')}
          </Button>
        </fieldset>

        <div>
          <label htmlFor="prereg-notes" className="mb-1 block text-sm font-medium text-slate-800">
            {t('prereg.field.notes')}
          </label>
          <textarea id="prereg-notes" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm" />
        </div>
        <CheckboxField label={t('prereg.create.sendEmail')} hint={t('prereg.create.sendEmailHint')} checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <Button type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('prereg.create.submit', { count: rows.length })}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
